import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { usePublishedArticles, useActiveAuthors, useLatestArticlesByAuthors } from "@/hooks/usePublicArticles";
import { useCategoriesDB } from "@/hooks/useCategoriesDB";
import type { Article, Author } from "@/mocks/articles";
import AdSlotDisplay from "@/components/feature/AdSlotDisplay";
import NewsletterBox from "@/components/feature/NewsletterBox";

const CATEGORY_ICON_MAP: Record<string, string> = {
  schweiz: "ri-map-pin-line",
  finanzen: "ri-money-dollar-circle-line",
  wirtschaft: "ri-bar-chart-box-line",
  technologie: "ri-cpu-line",
  digital: "ri-cpu-line",
  politik: "ri-government-line",
  gesundheit: "ri-heart-pulse-line",
  versicherung: "ri-shield-check-line",
  arbeit: "ri-briefcase-line",
  deals: "ri-price-tag-3-line",
  meinung: "ri-chat-quote-line",
  kolumnen: "ri-quill-pen-line",
  migration: "ri-user-community-line",
  bildung: "ri-graduation-cap-line",
  sport: "ri-football-line",
  kultur: "ri-movie-line",
  umwelt: "ri-earth-line",
};

function getCategoryIcon(slug: string): string {
  for (const [key, icon] of Object.entries(CATEGORY_ICON_MAP)) {
    if (slug.includes(key)) return icon;
  }
  return "ri-folder-line";
}

/* ─── Compact Article Card (stretches vertically to fill parent) ─── */
function CompactCard({ article, priority = false }: { article: Article; priority?: boolean }) {
  return (
    <Link to={`/artikel/${article.slug}`} className="group block h-full flex flex-col">
      <div className="relative overflow-hidden rounded-md mb-2 aspect-[16/9]">
        <img
          src={article.imageUrl}
          alt={article.imageAlt || article.title}
          loading={priority ? "eager" : "lazy"}
          width={400}
          height={250}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute top-2 left-2">
          <span className="px-1.5 py-0.5 text-[10px] font-semibold text-white bg-hj-red/90 rounded uppercase tracking-wide">
            {article.category}
          </span>
        </div>
      </div>
      <h3 className="text-[15px] font-bold text-hj-dark leading-snug group-hover:text-hj-red transition-colors line-clamp-2 font-heading">
        {article.title}
      </h3>
      <div className="flex items-center gap-2 mt-1.5 text-xs text-hj-muted font-body">
        <span>{article.author}</span>
        <span className="w-0.5 h-0.5 rounded-full bg-hj-muted" />
        <time dateTime={article.publishedAt}>{new Date(article.publishedAt).toLocaleDateString("de-CH")}</time>
      </div>
    </Link>
  );
}

/* ─── Horizontal List Item ─── */
function ListItem({ article, index }: { article: Article; index: number }) {
  return (
    <Link to={`/artikel/${article.slug}`} className="group flex gap-3 py-2.5 border-b border-hj-gray-border/60 last:border-0">
      <span className="text-lg font-bold text-hj-red/15 shrink-0 w-5 font-heading leading-tight mt-0.5">{index + 1}</span>
      <div className="flex-1 min-w-0">
        <span className="text-[10px] font-semibold text-hj-red uppercase tracking-wide">{article.category}</span>
        <h4 className="text-sm font-semibold text-hj-dark leading-snug group-hover:text-hj-red transition-colors line-clamp-2 mt-0.5 font-heading">
          {article.title}
        </h4>
        <time dateTime={article.publishedAt} className="text-[11px] text-hj-muted mt-0.5 block font-body">
          {new Date(article.publishedAt).toLocaleDateString("de-CH")}
        </time>
      </div>
    </Link>
  );
}

/* ─── Compact Section Header ─── */
function SectionHeader({ title, path }: { title: string; path: string }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-base md:text-lg font-bold text-hj-dark font-heading">{title}</h2>
      <Link to={path} className="text-[11px] md:text-xs font-medium text-hj-red hover:text-hj-red-light transition-colors flex items-center gap-0.5 font-body whitespace-nowrap">
        Alle <i className="ri-arrow-right-line" />
      </Link>
    </div>
  );
}

/* ─── More News Compact List ─── */
function MoreNewsList({ articles }: { articles: Article[] }) {
  const heroIds = new Set(
    articles
      .filter((a) => a.showOnHomepageSlider || a.isFeatured)
      .slice(0, 5)
      .map((a) => a.id)
  );
  const gridIds = new Set(articles.slice(0, 4).map((a) => a.id));
  const remaining = articles.filter((a) => !heroIds.has(a.id) && !gridIds.has(a.id)).slice(0, 6);
  if (remaining.length === 0) return null;

  return (
    <div className="mt-4">
      <div className="flex items-center justify-between mb-2.5">
        <h3 className="text-sm font-bold text-hj-dark font-heading">Weitere Nachrichten</h3>
        <Link to="/" className="text-[11px] font-medium text-hj-red hover:text-hj-red-light transition-colors flex items-center gap-0.5 font-body whitespace-nowrap">
          Alle <i className="ri-arrow-right-line" />
        </Link>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2">
        {remaining.map((article) => (
          <Link key={article.id} to={`/artikel/${article.slug}`} className="group flex gap-2.5 items-start">
            <div className="w-14 h-10 shrink-0 overflow-hidden rounded min-w-[56px]">
              <img
                src={article.imageUrl}
                alt={article.imageAlt || article.title}
                loading="lazy"
                width={56}
                height={40}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-[9px] font-semibold text-hj-red uppercase tracking-wide">{article.category}</span>
                <span className="w-0.5 h-0.5 rounded-full bg-hj-muted" />
                <time dateTime={article.publishedAt} className="text-[9px] text-hj-muted font-body">
                  {new Date(article.publishedAt).toLocaleDateString("de-CH")}
                </time>
              </div>
              <h4 className="text-[13px] font-semibold text-hj-dark leading-snug group-hover:text-hj-red transition-colors line-clamp-2 mt-0.5 font-heading">
                {article.title}
              </h4>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

/* ─── Compact Hero Slider ─── */
function CompactHeroSlider({ articles }: { articles: Article[] }) {
  const featured = articles.filter((a) => a.showOnHomepageSlider || a.isFeatured).slice(0, 5);
  const display = featured.length > 0 ? featured : articles.slice(0, 5);
  const [current, setCurrent] = useState(0);

  const next = useCallback(() => setCurrent((p) => (p + 1) % display.length), [display.length]);
  const prev = useCallback(() => setCurrent((p) => (p - 1 + display.length) % display.length), [display.length]);

  useEffect(() => {
    if (display.length <= 1) return;
    const t = setInterval(next, 6000);
    return () => clearInterval(t);
  }, [next, display.length]);

  if (display.length === 0) return null;

  return (
    <div className="relative h-[340px] lg:h-full overflow-hidden rounded-lg">
      {display.map((article, index) => (
        <div
          key={article.id}
          className={`absolute inset-0 transition-opacity duration-700 ${index === current ? "opacity-100 z-10" : "opacity-0 z-0"}`}
        >
          <img
            src={article.imageUrl}
            alt={article.imageAlt || article.title}
            loading={index === 0 ? "eager" : "lazy"}
            width={700}
            height={350}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-hj-dark via-hj-dark/50 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
            <span className="inline-flex items-center leading-none px-2 py-0.5 text-[10px] font-semibold text-white bg-hj-red rounded uppercase tracking-wide mb-2">
              {article.category}
            </span>
            <h2 className="text-lg md:text-2xl font-bold text-white leading-snug mb-1.5 font-heading line-clamp-2">
              {article.title}
            </h2>
            <div className="flex items-center gap-2 text-xs text-white/60 font-body">
              <span>{article.author}</span>
              <span className="w-0.5 h-0.5 rounded-full bg-white/40" />
              <time dateTime={article.publishedAt}>{new Date(article.publishedAt).toLocaleDateString("de-CH")}</time>
            </div>
          </div>
        </div>
      ))}
      {display.length > 1 && (
        <>
          <button
            onClick={prev}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-20 w-6 h-6 flex items-center justify-center rounded-full bg-black/30 text-white hover:bg-black/50 transition-colors cursor-pointer"
            aria-label="Vorheriger"
          >
            <i className="ri-arrow-left-line text-xs" />
          </button>
          <button
            onClick={next}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-20 w-6 h-6 flex items-center justify-center rounded-full bg-black/30 text-white hover:bg-black/50 transition-colors cursor-pointer"
            aria-label="Nächster"
          >
            <i className="ri-arrow-right-line text-xs" />
          </button>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-20 flex gap-1.5">
            {display.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                className={`h-1.5 rounded-full transition-all cursor-pointer ${i === current ? "bg-white w-4" : "bg-white/40 w-1.5 hover:bg-white/60"}`}
                aria-label={`Artikel ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ─── 5-Column Grid: single horizontal row, full-width below hero ─── */
function HeroGrid({ articles }: { articles: Article[] }) {
  const latest = articles.slice(0, 5);
  if (latest.length === 0) return null;

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
      {latest.map((article, i) => (
        <CompactCard key={article.id} article={article} priority={i < 2} />
      ))}
    </div>
  );
}

/* ─── Main News Grid Section ─── */
function MainSliderSection({ articles }: { articles: Article[] }) {
  const heroIds = new Set(
    articles
      .filter((a) => a.showOnHomepageSlider || a.isFeatured)
      .slice(0, 5)
      .map((a) => a.id)
  );
  const candidates = articles
    .filter((a) => !heroIds.has(a.id))
    .sort((a, b) => b.views - a.views)
    .slice(0, 6);
  const trending = [...articles].sort((a, b) => b.views - a.views).slice(0, 5);

  return (
    <section className="py-6 md:py-8 bg-hj-gray-light">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row gap-4 md:gap-5">
          <div className="flex-1">
            <SectionHeader title="Top Story" path="/schweiz" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
              {candidates.map((article, i) => (
                <CompactCard key={article.id} article={article} priority={i < 3} />
              ))}
            </div>
          </div>
          <div className="w-full lg:w-72 shrink-0 lg:self-stretch">
            <div className="bg-white rounded-lg border border-hj-gray-border p-3 md:p-4 h-full flex flex-col">
              <div className="flex-1">
                <MiniWidgetSlider articles={trending} />
              </div>
              <div className="mt-3 pt-3 border-t border-hj-gray-border/40">
                <AdSlotDisplay position="sidebar_top" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
function MiniWidgetSlider({ articles }: { articles: Article[] }) {
  const display = articles.slice(0, 4);
  if (display.length === 0) return null;

  return (
    <>
      <h3 className="text-[10px] font-bold uppercase tracking-wider text-hj-dark mb-3 flex items-center gap-1.5 font-body">
        <i className="ri-fire-line text-hj-red" />
        Trending
      </h3>
      <div className="space-y-0">
        {display.map((article, i) => (
          <ListItem key={article.id} article={article} index={i} />
        ))}
      </div>
    </>
  );
}

/* ─── Breaking News Ticker ─── */
function BreakingNewsTicker({ articles }: { articles: Article[] }) {
  const breaking = articles.filter((a) => a.isBreaking).slice(0, 8);
  if (breaking.length === 0) return null;

  // Duplicate for seamless loop
  const items = [...breaking, ...breaking];

  return (
    <div className="bg-hj-red overflow-hidden h-7 md:h-8">
      <div className="flex items-center h-full">
        {/* Label */}
        <div className="shrink-0 h-full flex items-center px-2 md:px-3 bg-hj-red-dark/40 gap-1">
          <i className="ri-flashlight-fill text-white text-[10px] md:text-xs" />
          <span className="text-[10px] md:text-xs font-bold text-white uppercase tracking-wider whitespace-nowrap font-body">Breaking</span>
        </div>
        {/* Scrolling headlines */}
        <div className="flex-1 overflow-hidden h-full">
          <div className="flex animate-marquee h-full items-center">
            {items.map((article, i) => (
              <Link
                key={`${article.id}-${i}`}
                to={`/artikel/${article.slug}`}
                className="inline-flex items-center gap-2 px-3 md:px-5 h-full shrink-0"
              >
                <span className="w-1 h-1 rounded-full bg-white/60 shrink-0" />
                <span className="text-xs md:text-sm text-white whitespace-nowrap font-body hover:text-white/80 transition-colors">
                  {article.title}
                </span>
                <time dateTime={article.publishedAt} className="text-[9px] md:text-[11px] text-white/50 shrink-0 hidden sm:inline">
                  {new Date(article.publishedAt).toLocaleDateString("de-CH")}
                </time>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Mobile Hero ─── */
function MobileHero({ articles }: { articles: Article[] }) {
  const featured = articles.filter((a) => a.isFeatured || a.showOnHomepageSlider).slice(0, 5);
  const display = featured.length > 0 ? featured : articles.slice(0, 5);
  const [current, setCurrent] = useState(0);

  const next = useCallback(() => setCurrent((p) => (p + 1) % display.length), [display.length]);
  const prev = useCallback(() => setCurrent((p) => (p - 1 + display.length) % display.length), [display.length]);

  useEffect(() => {
    if (display.length <= 1) return;
    const t = setInterval(next, 6000);
    return () => clearInterval(t);
  }, [next, display.length]);

  if (display.length === 0) return null;

  return (
    <div className="relative h-[220px] overflow-hidden rounded-lg">
      {display.map((article, index) => (
        <div
          key={article.id}
          className={`absolute inset-0 transition-opacity duration-700 ${index === current ? "opacity-100 z-10" : "opacity-0 z-0"}`}
        >
          <img src={article.imageUrl} alt={article.imageAlt || article.title} loading={index === 0 ? "eager" : "lazy"} width={640} height={280} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-hj-dark via-hj-dark/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-3 pb-4">
            <span className="inline-flex items-center leading-none px-2 py-0.5 text-[10px] font-semibold text-white bg-hj-red rounded uppercase tracking-wide mb-1.5">
              {article.category}
            </span>
            <h1 className="text-base font-bold text-white leading-snug font-heading line-clamp-2">{article.title}</h1>
            <div className="flex items-center gap-2 mt-1.5 text-xs text-white/60">
              <span>{article.author}</span>
              <span className="w-0.5 h-0.5 rounded-full bg-white/40" />
              <time dateTime={article.publishedAt}>{new Date(article.publishedAt).toLocaleDateString("de-CH")}</time>
            </div>
          </div>
        </div>
      ))}

      {/* Nav arrows */}
      {display.length > 1 && (
        <>
          <button onClick={prev} className="absolute left-2 top-1/2 -translate-y-1/2 z-20 w-7 h-7 flex items-center justify-center rounded-full bg-black/30 text-white hover:bg-black/50 transition-colors cursor-pointer" aria-label="Vorheriger">
            <i className="ri-arrow-left-line text-sm" />
          </button>
          <button onClick={next} className="absolute right-2 top-1/2 -translate-y-1/2 z-20 w-7 h-7 flex items-center justify-center rounded-full bg-black/30 text-white hover:bg-black/50 transition-colors cursor-pointer" aria-label="Nächster">
            <i className="ri-arrow-right-line text-sm" />
          </button>
        </>
      )}
    </div>
  );
}

/* ─── Mobile Compact News Grid ─── */
function MobileNewsGrid({ articles }: { articles: Article[] }) {
  const latest = articles.slice(0, 5);
  if (latest.length === 0) return null;

  return (
    <div className="md:hidden mb-3">
      <div className="flex gap-3 overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-1 -mx-4 px-4">
        {latest.map((article) => (
          <Link key={article.id} to={`/artikel/${article.slug}`} className="group block shrink-0 snap-start w-[160px]">
            <div className="relative overflow-hidden rounded-md aspect-[16/10] mb-1.5">
              <img
                src={article.imageUrl}
                alt={article.imageAlt || article.title}
                loading="lazy"
                width={160}
                height={100}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute top-1.5 left-1.5">
                <span className="px-1.5 py-0.5 text-[9px] font-semibold text-white bg-hj-red/90 rounded uppercase tracking-wide">
                  {article.category}
                </span>
              </div>
            </div>
            <h3 className="text-[13px] font-bold text-hj-dark leading-snug group-hover:text-hj-red transition-colors line-clamp-2 font-heading">
              {article.title}
            </h3>
            <div className="flex items-center gap-1.5 mt-1 text-[10px] text-hj-muted font-body">
              <span>{article.author}</span>
              <span className="w-0.5 h-0.5 rounded-full bg-hj-muted" />
              <time dateTime={article.publishedAt}>{new Date(article.publishedAt).toLocaleDateString("de-CH")}</time>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

/* ─── Compact Hero Sidebar (unified: Top News + ad) ─── */
function HeroSidebar({ articles }: { articles: Article[] }) {
  const top = articles.slice(0, 5);
  if (top.length === 0) return null;

  return (
    <div>
      {/* Top News — ultra-compact list */}
      <div>
        <h3 className="text-[10px] font-bold uppercase tracking-widest text-hj-muted mb-2 font-body flex items-center gap-1.5">
          <i className="ri-flashlight-line text-hj-red" />
          Top News
        </h3>
        <div className="space-y-0">
          {top.map((article) => (
            <Link
              key={article.id}
              to={`/artikel/${article.slug}`}
              className="group block py-2 border-b border-hj-gray-border/40 last:border-0"
            >
              <span className="text-[9px] font-semibold text-hj-red uppercase tracking-wider">{article.category}</span>
              <h4 className="text-[13px] font-semibold text-hj-dark leading-snug group-hover:text-hj-red transition-colors line-clamp-2 mt-0.5 font-heading">
                {article.title}
              </h4>
              <div className="flex items-center gap-1.5 mt-1">
                <span className="text-[11px] text-hj-muted font-body">{article.author}</span>
                <span className="w-0.5 h-0.5 rounded-full bg-hj-muted" />
                <time dateTime={article.publishedAt} className="text-[11px] text-hj-muted font-body">
                  {new Date(article.publishedAt).toLocaleDateString("de-CH")}
                </time>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Top News Mini Widget ─── */
function TopNewsMini({ articles }: { articles: Article[] }) {
  const top = articles.slice(0, 5);
  if (top.length === 0) return null;
  return (
    <div className="bg-white rounded-lg border border-hj-gray-border p-3 md:p-4">
      <h3 className="text-[10px] font-bold uppercase tracking-wider text-hj-dark mb-3 flex items-center gap-1.5 font-body">
        <i className="ri-flashlight-line text-hj-red" />
        Top News
      </h3>
      <div className="space-y-0">
        {top.map((article, i) => (
          <ListItem key={article.id} article={article} index={i} />
        ))}
      </div>
    </div>
  );
}

/* ─── Category Section (Dense) ─── */
function DenseCategorySection({
  title, slug, articles,
}: {
  title: string; slug: string; articles: Article[];
}) {
  const catArticles = articles.filter((a) => a.categorySlug === slug).slice(0, 4);
  if (catArticles.length === 0) return null;

  const main = catArticles[0];
  const rest = catArticles.slice(1);

  return (
    <section className="py-6 md:py-8">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <SectionHeader title={title} path={`/${slug}`} />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Main card */}
          <Link to={`/artikel/${main.slug}`} className="group block">
            <div className="relative overflow-hidden rounded-lg aspect-[16/10] mb-2.5">
              <img src={main.imageUrl} alt={main.imageAlt || main.title} loading="eager" width={640} height={360} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute top-2 left-2">
                <span className="px-1.5 py-0.5 text-[10px] font-semibold text-white bg-hj-red/90 rounded uppercase tracking-wide">{main.category}</span>
              </div>
            </div>
            <h3 className="text-base md:text-lg font-bold text-hj-dark leading-snug group-hover:text-hj-red transition-colors line-clamp-2 font-heading">{main.title}</h3>
            <p className="text-xs text-hj-gray mt-1 line-clamp-2 font-body hidden sm:block">{main.excerpt}</p>
            <div className="flex items-center gap-2 mt-2 text-[11px] text-hj-muted font-body">
              <span className="font-medium text-hj-dark">{main.author}</span>
              <time dateTime={main.publishedAt}>{new Date(main.publishedAt).toLocaleDateString("de-CH")}</time>
            </div>
          </Link>
          {/* Side list */}
          <div className="space-y-0">
            {rest.map((article) => (
              <Link key={article.id} to={`/artikel/${article.slug}`} className="group flex gap-2.5 py-2 border-b border-hj-gray-border/60 last:border-0">
                <div className="w-16 h-11 shrink-0 overflow-hidden rounded min-w-[64px]">
                  <img src={article.imageUrl} alt={article.imageAlt || article.title} loading="lazy" width={64} height={44} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-[10px] font-semibold text-hj-red uppercase tracking-wide">{article.category}</span>
                  <h4 className="text-sm font-semibold text-hj-dark leading-snug group-hover:text-hj-red transition-colors line-clamp-2 mt-0.5 font-heading">{article.title}</h4>
                  <time dateTime={article.publishedAt} className="text-[11px] text-hj-muted mt-0.5 block">{new Date(article.publishedAt).toLocaleDateString("de-CH")}</time>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Authors Section ─── */
function AuthorsSection() {
  const { authors, loading } = useActiveAuthors(8);
  const slugs = authors.map((a) => a.slug);
  const { latestMap } = useLatestArticlesByAuthors(slugs);
  const [currentPage, setCurrentPage] = useState(0);

  if (!loading && authors.length === 0) return null;

  const itemsPerPage = 4;
  const totalPages = Math.ceil(authors.length / itemsPerPage);
  const visibleAuthors = authors.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage);

  const prevPage = () => setCurrentPage((p) => Math.max(0, p - 1));
  const nextPage = () => setCurrentPage((p) => Math.min(totalPages - 1, p + 1));

  return (
    <section className="py-4 sm:py-5 md:py-8 bg-hj-gray-light">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6">
        <h2 className="text-sm sm:text-base md:text-xl font-bold text-hj-dark text-center mb-2 md:mb-6 font-heading">
          Helvetik Journal Autoren
        </h2>

        {loading ? (
          <div className="flex justify-center gap-2 md:gap-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex flex-col items-center animate-pulse w-[88px] md:w-auto">
                <div className="w-16 h-16 md:w-24 md:h-24 rounded-full bg-white mb-1.5" />
                <div className="h-2.5 w-14 md:w-24 bg-white rounded mb-0.5" />
                <div className="h-2.5 w-18 md:w-32 bg-white rounded" />
              </div>
            ))}
          </div>
        ) : (
          <div className="relative flex items-center gap-0.5 md:gap-2">
            {/* Prev button */}
            <button
              onClick={prevPage}
              disabled={currentPage === 0}
              className="shrink-0 w-6 h-6 md:w-8 md:h-8 flex items-center justify-center rounded-full border border-hj-gray-border bg-white text-hj-dark hover:border-hj-red hover:text-hj-red transition-colors disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
              aria-label="Zurück"
            >
              <i className="ri-arrow-left-s-line text-sm md:text-lg" />
            </button>

            {/* Authors — horizontal scrollable row on mobile, grid on desktop */}
            <div className="flex-1 overflow-x-auto scrollbar-hide md:overflow-visible">
              <div className="flex gap-2 md:gap-4 md:grid md:grid-cols-2 lg:grid-cols-4 min-w-0">
                {visibleAuthors.map((author) => {
                  const latest = latestMap?.[author.slug];
                  return (
                    <div key={author.slug} className="flex flex-col items-center text-center shrink-0 w-[88px] md:w-auto">
                      <Link to={`/autor/${author.slug}`} className="group block w-full">
                        <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 lg:w-28 lg:h-28 mx-auto rounded-full overflow-hidden bg-hj-gray-light border-2 border-hj-gray-border group-hover:border-hj-red transition-colors mb-1 md:mb-2.5">
                          {author.image ? (
                            <img src={author.image} alt={author.name} loading="lazy" width={96} height={96} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-hj-gray">
                              <i className="ri-user-line text-lg md:text-3xl" />
                            </div>
                          )}
                        </div>
                        <h3 className="text-[11px] md:text-sm font-semibold text-hj-dark font-heading truncate group-hover:text-hj-red transition-colors">
                          {author.name}
                        </h3>
                        {latest && (
                          <p className="text-[9px] md:text-xs text-hj-muted mt-0.5 line-clamp-2 font-body leading-snug">
                            {latest.title}
                          </p>
                        )}
                      </Link>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Next button */}
            <button
              onClick={nextPage}
              disabled={currentPage === totalPages - 1}
              className="shrink-0 w-6 h-6 md:w-8 md:h-8 flex items-center justify-center rounded-full border border-hj-gray-border bg-white text-hj-dark hover:border-hj-red hover:text-hj-red transition-colors disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
              aria-label="Weiter"
            >
              <i className="ri-arrow-right-s-line text-sm md:text-lg" />
            </button>
          </div>
        )}

        {/* Dot indicators */}
        {!loading && totalPages > 1 && (
          <div className="flex items-center justify-center gap-1 md:gap-2 mt-1.5 md:mt-4">
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentPage(i)}
                className={`w-1.5 h-1.5 md:w-2 md:h-2 rounded-full transition-colors cursor-pointer ${
                  i === currentPage ? "bg-hj-red" : "bg-hj-gray-border hover:bg-hj-muted"
                }`}
                aria-label={`Seite ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

/* ─── Opinion Section ─── */
function OpinionSection({ articles }: { articles: Article[] }) {
  const opinionArticles = articles.filter((a) => a.isOpinion || a.isColumn || a.categorySlug === "meinung" || a.categorySlug === "kolumnen").slice(0, 3);
  if (opinionArticles.length === 0) return null;

  return (
    <section className="py-6 md:py-8 bg-hj-navy">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base md:text-lg font-bold text-white font-heading">Meinung & Kolumnen</h2>
          <Link to="/meinung" className="text-[11px] md:text-xs font-medium text-white/70 hover:text-white transition-colors flex items-center gap-0.5 font-body whitespace-nowrap">
            Alle <i className="ri-arrow-right-line" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
          {opinionArticles.map((article) => (
            <Link key={article.id} to={`/artikel/${article.slug}`} className="group block bg-white/5 rounded-lg p-3 md:p-4 hover:bg-white/10 transition-colors border border-white/10">
              <div className="flex items-center gap-2.5 mb-2.5">
                <div className="w-8 h-8 rounded-full bg-hj-red/20 flex items-center justify-center text-[10px] font-bold text-white shrink-0">
                  {article.author.split(" ").map((n) => n[0]).join("")}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white font-body truncate">{article.author}</p>
                  <p className="text-[11px] text-white/50 font-body truncate">{article.authorRole}</p>
                </div>
              </div>
              <h3 className="text-sm md:text-base font-bold text-white leading-snug group-hover:text-hj-red-light transition-colors line-clamp-2 mb-1.5 font-heading">{article.title}</h3>
              <p className="text-xs text-white/60 line-clamp-2 font-body hidden sm:block">{article.excerpt}</p>
              <div className="flex items-center gap-2 mt-2.5 text-[11px] text-white/40 font-body">
                <time dateTime={article.publishedAt}>{new Date(article.publishedAt).toLocaleDateString("de-CH")}</time>
                <span className="w-0.5 h-0.5 rounded-full bg-white/30" />
                <span>{article.readTime}</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Homepage ─── */
export default function Home() {
  const { articles, loading: articlesLoading, error: articlesError } = usePublishedArticles();
  const { categories } = useCategoriesDB();

  // Categories that should show on homepage, sorted by homepageOrder
  const homepageCategories = categories
    .filter((c) => c.showOnHomepage && c.isActive)
    .sort((a, b) => a.homepageOrder - b.homepageOrder);

  return (
    <div className="min-h-screen bg-white">
      {/* ── Breaking News Ticker ── */}
      <div className="pt-[80px] lg:pt-[130px] relative z-40">
        <BreakingNewsTicker articles={articles} />
      </div>

      {/* ── Top Hero ── */}
      <section className="pb-3 md:pb-5 mt-0 md:mt-4">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          {articlesError && (
            <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
              <strong>Fehler:</strong> {articlesError}
            </div>
          )}

          {/* Mobile hero */}
          <div className="md:hidden mb-3">
            {articlesLoading ? <div className="h-[220px] rounded-lg bg-hj-gray-light animate-pulse" /> : <MobileHero articles={articles} />}
          </div>

          {/* Mobile ad slot after hero */}
          {!articlesLoading && (
            <div className="md:hidden mb-3">
              <AdSlotDisplay position="mobile_after_hero" />
            </div>
          )}

          {/* Mobile compact news grid before authors */}
          {articlesLoading ? (
            <div className="md:hidden mb-2 grid grid-cols-2 gap-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-[140px] rounded-md bg-hj-gray-light animate-pulse" />
              ))}
            </div>
          ) : (
            <MobileNewsGrid articles={articles} />
          )}

          {/* Desktop: slider + sidebar row, then full-width 5-card grid */}
          <div className="hidden md:flex flex-col gap-4">
            {/* Row 1: Hero slider + Top News */}
            <div className="flex flex-col lg:flex-row gap-5 lg:items-stretch">
              <div className="flex-1">
                {articlesLoading ? (
                  <div className="h-[340px] lg:h-full rounded-lg bg-hj-gray-light animate-pulse" />
                ) : (
                  <CompactHeroSlider articles={articles} />
                )}
              </div>
              <div className="w-full lg:w-80 shrink-0">
                <div className="bg-white rounded-lg border border-hj-gray-border p-4">
                  {articlesLoading ? (
                    <div className="space-y-3">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} className="h-14 bg-hj-gray-light rounded animate-pulse" />
                      ))}
                    </div>
                  ) : (
                    <HeroSidebar articles={articles} />
                  )}
                </div>
              </div>
            </div>

            {/* Row 2: Full-width 5-card grid */}
            {articlesLoading ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="h-48 rounded-md bg-hj-gray-light animate-pulse" />
                ))}
              </div>
            ) : (
              <HeroGrid articles={articles} />
            )}

            {/* Full-width leaderboard ad under 5-card grid */}
            <div className="mt-5 md:mt-6 -mx-4 md:-mx-6">
              <AdSlotDisplay position="hero_grid_bottom" className="py-3 md:py-5 bg-hj-gray-light/50" />
            </div>
          </div>
        </div>
      </section>

      {/* ── Mobile ad above Authors ── */}
      <div className="md:hidden max-w-7xl mx-auto px-4 pt-1">
        <AdSlotDisplay position="mobile_above_authors" />
      </div>

      {/* ── Mobile ad between homepage cards and main section ── */}
      <div className="md:hidden max-w-7xl mx-auto px-4 py-2">
        <AdSlotDisplay position="mobile_between_cards_home" />
      </div>

      {/* ── Authors Section (now above Main Slider) ── */}
      <AuthorsSection />

      {/* ── Main News Slider Section ── */}
      <MainSliderSection articles={articles} />

      {/* ── Dynamic Category sections ── */}
      {homepageCategories.map((cat, idx) => (
        <div key={cat.id}>
          {idx === 1 && (
            <div className="md:hidden max-w-7xl mx-auto px-4 py-2">
              <AdSlotDisplay position="mobile_between_sections" />
            </div>
          )}
          {idx === 2 && (
            <div className="md:hidden max-w-7xl mx-auto px-4 py-2">
              <AdSlotDisplay position="mobile_between_sections" />
            </div>
          )}
          {idx === 3 && (
            <div className="md:hidden max-w-7xl mx-auto px-4 py-2">
              <AdSlotDisplay position="mobile_between_sections" />
            </div>
          )}
          <DenseCategorySection title={cat.name} slug={cat.slug} articles={articles} />
        </div>
      ))}

      <OpinionSection articles={articles} />

      {/* ── Mobile ad before footer ── */}
      <div className="md:hidden max-w-7xl mx-auto px-4 py-2">
        <AdSlotDisplay position="mobile_before_footer" />
      </div>

      {/* Empty state */}
      {!articlesLoading && articles.length === 0 && !articlesError && (
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
            <i className="ri-newspaper-line text-5xl text-hj-gray-border mb-4 block" />
            <h2 className="text-xl font-bold text-hj-dark mb-2 font-heading">Noch keine Artikel vorhanden</h2>
            <p className="text-hj-gray font-body">Erstellen und veröffentlichen Sie Artikel im Admin-Panel.</p>
            <Link to="/admin/artikel-neu" className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 bg-hj-red text-white text-sm font-medium rounded-lg hover:bg-hj-red-light transition-colors">
              <i className="ri-add-line" />Ersten Artikel erstellen
            </Link>
          </div>
        </section>
      )}

      {/* Newsletter */}
      <section className="py-8 md:py-10 bg-hj-dark">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <NewsletterBox variant="dark" title="Bleiben Sie informiert." description="Erhalten Sie die wichtigsten Nachrichten aus der Schweiz direkt in Ihr Postfach." />
        </div>
      </section>
    </div>
  );
}